import { fromPromise } from 'xstate';
import { api } from '~/features/cart/state-machine/backend-access/api';
import type {
  CancelCartData,
  CartResponseData,
  ChangeDeliveryDateData,
  MutatePositionPayload,
  PositionIntent,
  PositionIntentActorResponse,
  SendCartUserData
} from '~/features/cart/state-machine/types';
import {
  publishShoppingCartBroadcast,
  type RemovedCartPositionTrackingData
} from '~/features/cart/cartBroadcast';
import {
  HAS_BLOCKED_POSITIONS,
  PROLIX_ERROR_IDENTIFIER
} from '../../../../common/types/prolixApplicationError';
import type {
  IOrderCurrentDTOData,
  IOrderPositionDTOData
} from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { normalizeCartError } from '~/features/cart/state-machine/cartError';

export const fetchCartStatusActor = fromPromise(
  withErrorWrappingForStateMachine(async ({ input: frontendOrderId }: { input?: number }) => {
    return await api().fetchCartStatus(frontendOrderId);
  })
);

export const fetchOrderActor = fromPromise(
  withErrorWrappingForStateMachine(async ({ input: frontendOrderId }: { input?: number }) => {
    // Dieser Actor wird aufgerufen am Anfang, wenn die State Machine initialisiert wird, um den aktuellen Warenkorb zu laden. => keine orderId wird übergeben.
    // Später wird dieser Actor auch aufgerufen, bevor eine Mutation vorgenommen wird, um möglichst sicher den aktuellen State vom Server zu haben.
    //    => dann wird eine orderId übergeben. Wenn die orderId nicht mehr dem aktuellen Warenkorb entspricht, dann wirft der Server einen Fehler.
    const cartResponse = await api().fetchCurrentOrder(frontendOrderId);
    return cartResponse;
  })
);
export const refreshOrderActor = fromPromise(
  withErrorWrappingForStateMachine(async ({ input: frontendOrderId }: { input?: number }) => {
    const cartResponse = await api().refreshCurrentOrder(frontendOrderId);
    return cartResponse;
  })
);
export const createOrderActor = fromPromise(
  withErrorWrappingForStateMachine(async ({ input }: { input: string }) => {
    const response = await api().createNewOrder(input);
    return response;
  })
);

export const processPositionIntentActor = fromPromise(
  withErrorWrappingForStateMachine(
    async ({
      input
    }: {
      input: { orderId: number; currentOrder: IOrderCurrentDTOData | undefined; positionIntent: PositionIntent };
    }) => {
      const positionIntent = input.positionIntent;
      let cartResponse: CartResponseData;
      let articleDescriptionForErrorMessage: string | undefined;
      let isCsvImport = false;
      if (positionIntent.operation === 'ADD') {
        // Bei dem Hinzufügen von Artikeln in den Warenkorb können auf dem Backend diverse Fehler auftreten (nicht verfügbar, nicht lieferbar etc).
        // Das Backend liefert dann in der Regel nur eine Fehlermeldung mit der Artikelnummer zurück.
        // In Szenarien wo mehrere Artikel "automatisch" zu dem Warenkorb hinzugefügt werden (Vorlagen, Salesorderset ...), ist es dem Benutzer oft nicht klar, welcher Artikel tatsächlich das Problem hatte.
        // Daher reichert das Frontend hier die Message mit der Beschreibung des Artikels an, welche dann im Fehlerdialog mit angezeigt werden kann
        articleDescriptionForErrorMessage = positionIntent.articleDescriptionForErrorMessage;
        cartResponse = await api().addPosition(
          input.orderId,
          positionIntent.articleNumber,
          positionIntent.quantity,
          positionIntent.externalArticle
        );
        publishShoppingCartBroadcast('ADD_INTENT_PROCESSED', {
          successfullyAdded: addPositionResponseSignalsSuccessfulAdd(cartResponse),
          addedWithAvailabilityOptions: !!cartResponse.positionAvailabilityOptions?.length,
          articleNumber: positionIntent.articleNumber,
          quantity: positionIntent.quantity,
          isExternalArticle: positionIntent.externalArticle,
          articleDescription: positionIntent.articleDescriptionForErrorMessage
        });
      } else if (positionIntent.operation === 'REMOVE') {
        cartResponse = await api().removePosition(positionIntent.positionId, positionIntent.externalArticle);
        publishPositionsRemovedIfSuccessful(cartResponse, input.currentOrder, position => {
          return !positionIntent.externalArticle && position.id === positionIntent.positionId;
        });
      } else if (positionIntent.operation === 'MUTATE') {
        const mutatePayload: MutatePositionPayload = {
          articleNumber: positionIntent.articleNumber,
          quantity: positionIntent.quantity,
          deliveryDate: positionIntent.deliveryDate,
          externalArticle: positionIntent.externalArticle
        };
        cartResponse = await api().mutatePosition(positionIntent.positionId, mutatePayload);
      } else if (positionIntent.operation === 'COPY_EXISTING_ORDER_TO_CART') {
        cartResponse = await api().copyExistingOrderToCart(input.orderId, positionIntent.existingOrderId);
      } else if (positionIntent.operation === 'COPY_TEMPLATE_ARTICLE_TO_CART') {
        cartResponse = await api().copyTemplateArticleToCart(input.orderId, positionIntent.templateId, positionIntent.selectedMainArticleIds);
      } else if (positionIntent.operation === 'COPY_SALESORDERSET_TO_CART') {
        cartResponse = await api().copySalesOrderSetArticleToCart(input.orderId, positionIntent.articles);
      } else if (positionIntent.operation === 'IMPORT_CSV_TO_CART') {
        cartResponse = await api().importCsvToCart(input.orderId, positionIntent.csvText);
        isCsvImport = true;
      } else {
        const exhaustiveCheck: never = positionIntent; // TypeScript trick to produce a compiler error if a case is missing
        throw new Error('Unknown operation for position intent¨: ' + exhaustiveCheck);
      }

      const positionIntentActorResponse: PositionIntentActorResponse = {
        ...cartResponse,
        articleDescriptionForErrorMessage,
        isCsvImport
      };
      return positionIntentActorResponse;
    }
  )
);

function addPositionResponseSignalsSuccessfulAdd(cartResponse: CartResponseData): boolean {
  if (cartResponse.expirationReason) return false;
  if (!cartResponse.prolixError) return true;

  return cartResponse[PROLIX_ERROR_IDENTIFIER] === HAS_BLOCKED_POSITIONS;
}

export function publishPositionsRemovedIfSuccessful(
  cartResponse: CartResponseData,
  order: IOrderCurrentDTOData | undefined,
  predicate: (position: IOrderPositionDTOData) => boolean = () => true
): void {
  if (cartResponse.expirationReason || cartResponse.prolixError || (cartResponse as { error?: unknown }).error) return;

  const groupedPositions = [...(order?.normalPositions ?? []), ...(order?.profitPlusPositions ?? [])];
  const positions: RemovedCartPositionTrackingData[] = groupedPositions
    .flatMap(group => group.positions)
    .filter(position => predicate(position))
    .filter(position => position.orderQuantity > 0)
    .map(position => ({
      articleNumber: position.articleBaseNo,
      quantity: position.orderQuantity,
      articleDescription: position.articleDescriptionText
    }));
  if (positions.length === 0) return;

  publishShoppingCartBroadcast('POSITIONS_REMOVED', { positions });
}

export const sendOrderActor = fromPromise(
  withErrorWrappingForStateMachine(async ({ input }: { input: { cartParams: SendCartUserData; orderId: number } }) => {
    const cartResponse = await api().sendCurrentOrder({ ...input.cartParams, orderId: input.orderId });
    return cartResponse;
  })
);
export const cancelOrderActor = fromPromise(
  withErrorWrappingForStateMachine(async ({ input }: { input: CancelCartData }) => {
    const cartResponse = await api().cancelCurrentOrder(input);
    return cartResponse;
  })
);
export const setDeliveryDateActor = fromPromise(
  withErrorWrappingForStateMachine(async ({ input }: { input: ChangeDeliveryDateData }) => {
    return await api().changeDeliveryDateOfCurrentOrder(input);
  })
);

function withErrorWrappingForStateMachine<TArgs extends any[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>
): (...args: TArgs) => Promise<TReturn> {
  return async (...args: TArgs): Promise<TReturn> => {
    try {
      return await fn(...args);
    } catch (e) {
      throw normalizeCartError(e);
    }
  };
}
